import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Alert, Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { ExpensesSubscriptionMembersDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  AddExpensesPlanEmployeesDocument,
  EmployeeSelectorCountDocument,
  ExpensesSubscriptionsFeatureMembershipDocument,
  GetExpensesPlanEmployeesDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface Props {
  disabled?: boolean
  onClose: () => void
}
const AddUsersModal: React.FC<Props> = ({ disabled, onClose }) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()

  const { data, error } = useQuery(GetExpensesPlanEmployeesDocument, {
    variables: { companyId: company.id },
  })

  const excludedIds = compact(
    data?.features.featureMembership?.members.map((m) => m.access.employee?.id)
  )

  const { data: employeeData, error: employeeError } = useQuery(EmployeeSelectorCountDocument, {
    skip: !data,
    variables: { companyId: company.id, excludeIds: excludedIds },
  })

  const [newSelectedEmployees, setNewSelectedEmployees] = useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])

  const [addPlanEmployees] = useMutation(AddExpensesPlanEmployeesDocument, {
    refetchQueries: [
      ExpensesSubscriptionMembersDatatableDocument,
      ExpensesSubscriptionsFeatureMembershipDocument,
    ],
  })

  const handleSubmit = async () => {
    if (disabled) return

    const result = await addPlanEmployees({
      variables: {
        employeeIds: newSelectedEmployees.map((e) => e.id),
        companyId: company.id,
      },
    })

    if (
      result.errors ||
      !result.data ||
      result.data.featuresMutations.updateMembersFeaturesFeatureMembership.errors.length > 0
    ) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('billing.subscription.plans.expenses.messages.error_adding_users'),
      })
    } else {
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('billing.subscription.plans.expenses.messages.users_added_successfully'),
      })
    }
  }

  const availableEmployees = employeeData?.employees?.employeeNamesConnection?.totalCount

  if (!data && !employeeData && !error && !employeeError) {
    return (
      <Modal
        title={i18n.t('billing.subscription.plans.expenses.actions.add_users')}
        illustration="add-user"
      >
        <Loading expand />
      </Modal>
    )
  }

  if (availableEmployees === 0)
    return (
      <Modal
        title={i18n.t('billing.subscription.plans.expenses.actions.add_users')}
        onClose={onClose}
        illustration="add-user"
        description={i18n.t(
          'billing.subscription.plans.expenses.add_users_modal.no_employees_description_v2'
        )}
        footer={
          <Box flexDirection="row" gap="s8">
            <Button type="secondary" onClick={onClose}>
              {i18n.t('common.cancel')}
            </Button>
          </Box>
        }
      >
        <Link.Router to={navigation.settings.plans.expenses.fullPath()} type="default">
          {i18n.t('billing.subscription.plans.expenses.add_users_modal.no_employees_action')}
        </Link.Router>
      </Modal>
    )

  return (
    <Modal
      title={i18n.t('billing.subscription.plans.expenses.actions.add_users')}
      onClose={onClose}
      illustration="add-user"
      description={i18n.t('billing.subscription.plans.expenses.add_users_modal.description')}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={disabled}>
            {newSelectedEmployees.length > 1
              ? i18n.t('billing.subscription.plans.expenses.add_users_modal.add_n_users', {
                  count: newSelectedEmployees.length,
                })
              : i18n.t('common.add')}
          </Button>
        </Box>
      }
      disableFocusTrap
    >
      {disabled ? (
        <Alert color="error">
          <Text size="300" weight="medium">
            {i18n.t('billing.subscription.plans.expenses.managed_by_partners.usage_alert_banner')}
          </Text>
        </Alert>
      ) : (
        <Box maxWidth="s400">
          <EmployeeSelector
            multiple
            label={i18n.t(
              'billing.subscription.plans.expenses.add_users_modal.employee_selector_label'
            )}
            excludeIds={excludedIds}
            value={newSelectedEmployees}
            onSelect={(selection) => setNewSelectedEmployees(selection)}
          />
        </Box>
      )}
    </Modal>
  )
}

export default AddUsersModal
